import axios from "axios";
import { FR_BASE_URL, FR_API_KEY } from "./frNetworkHelper";

const defaultFrSettings = {
  fr_enabled: false,
  fr_has_bypass_password: false,
  fr_has_photo_edit_password: false,
};

const ENROLLMENT_MAX_ATTEMPTS = 3;
const ENROLLMENT_BACKOFF_MS = [0, 300, 800];
const ENROLLMENT_TIMEOUT_MS = 10000;

const frHeaders = (business_id) => ({
  "x-business-id": business_id,
  "x-api-key": FR_API_KEY,
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Single GET /business/enrollment attempt.
 * Mirrors getdiskon-merchant-web application/helpers/fr_helper.php
 *
 * @returns {{ enrolled: 0|1|null, retry: boolean }}
 */
const frEnrollmentRequest = async (business_id) => {
  try {
    const res = await axios.get(`${FR_BASE_URL}business/enrollment`, {
      headers: frHeaders(business_id),
      timeout: ENROLLMENT_TIMEOUT_MS,
      validateStatus: () => true,
    });

    const { status, data } = res;

    if (status === 200) {
      if (data && Object.prototype.hasOwnProperty.call(data, "enrolled")) {
        return { enrolled: data.enrolled ? 1 : 0, retry: false };
      }
      return { enrolled: null, retry: true };
    }

    if (status === 401) {
      return { enrolled: null, retry: false };
    }

    if (status === 400) {
      return { enrolled: 0, retry: false };
    }

    if ([502, 503, 504].includes(status)) {
      return { enrolled: null, retry: true };
    }

    return { enrolled: null, retry: false };
  } catch {
    return { enrolled: null, retry: true };
  }
};

/**
 * Business-level FR enrollment check.
 *
 * @returns {Promise<0|1|null>} 1 = enrolled, 0 = not enrolled, null = check failed (fail closed)
 */
export const resolveFrEnrollment = async (business_id) => {
  const id = parseInt(business_id, 10);
  if (!id || id <= 0) {
    return 0;
  }

  for (let attempt = 0; attempt < ENROLLMENT_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await sleep(ENROLLMENT_BACKOFF_MS[attempt]);
    }

    const result = await frEnrollmentRequest(id);

    if (result.enrolled !== null) {
      return result.enrolled;
    }

    if (!result.retry) {
      return null;
    }
  }

  return null;
};

const fetchOutletFrSettings = (business_id, outlet_id) => {
  return axios
    .get(`${FR_BASE_URL}outlet-settings/${outlet_id}`, {
      headers: frHeaders(business_id),
    })
    .then((res) => ({
      fr_enabled: res.data.is_fr_enabled ?? false,
      fr_has_bypass_password: res.data.has_bypass_password ?? false,
      fr_has_photo_edit_password: res.data.has_photo_edit_password ?? false,
    }))
    .catch(() => ({ ...defaultFrSettings }));
};

/**
 * Resolves business enrollment + outlet FR settings into effective cashier flags.
 */
export const resolveFrConfig = async (business_id, outlet_id) => {
  const [enrollment, outletSettings] = await Promise.all([
    resolveFrEnrollment(business_id),
    fetchOutletFrSettings(business_id, outlet_id),
  ]);

  const has_face_recognition = enrollment === 1;

  return {
    has_face_recognition,
    fr_enabled: has_face_recognition && outletSettings.fr_enabled,
    fr_has_bypass_password: has_face_recognition
      ? outletSettings.fr_has_bypass_password
      : false,
    fr_has_photo_edit_password: has_face_recognition
      ? outletSettings.fr_has_photo_edit_password
      : false,
  };
};

export const verifyFrPassword = (business_id, outlet_id, password_type, plaintext) => {
  return axios
    .post(
      `${FR_BASE_URL}outlet-settings/${outlet_id}/verify`,
      { password_type, password: plaintext },
      { headers: { ...frHeaders(business_id), "Content-Type": "application/json" } }
    )
    .then((res) => res.data.valid === true)
    .catch(() => false);
};
